import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { assertPublicRateLimit } from "@/lib/rate-limit.server";

function publicClient() {
  return createClient<Database>(process.env.SUPABASE_URL!, process.env.SUPABASE_PUBLISHABLE_KEY!, {
    auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
  });
}

export const listMarketplace = createServerFn({ method: "POST" })
  .inputValidator((d) =>
    z
      .object({
        industry: z.string().optional(),
        country: z.string().optional(),
        q: z.string().optional(),
      })
      .partial()
      .parse(d ?? {}),
  )
  .handler(async ({ data }) => {
    await assertPublicRateLimit("marketplace:list", 60);
    const supabase = publicClient();
    let query = supabase
      .from("companies")
      .select(
        "id, name, description, industry, country, size, ai_score, seeking, twin_status, logo_url",
      )
      .eq("is_public", true)
      .order("ai_score", { ascending: false })
      .limit(60);
    if (data.industry) query = query.eq("industry", data.industry);
    if (data.country) query = query.eq("country", data.country);
    if (data.q) query = query.ilike("name", `%${data.q}%`);
    const { data: rows, error } = await query;
    if (error) throw error;
    return { companies: rows ?? [] };
  });

export const getPublicCompany = createServerFn({ method: "POST" })
  .inputValidator((d) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data }) => {
    await assertPublicRateLimit("marketplace:company", 60);
    const supabase = publicClient();
    const { data: company, error } = await supabase
      .from("companies")
      .select(
        "id, name, description, industry, country, size, ai_score, seeking, twin_status, personality, website, logo_url, pages_indexed, documents_indexed, knowledge_size, updated_at",
      )
      .eq("id", data.id)
      .eq("is_public", true)
      .maybeSingle();
    if (error) throw error;
    if (!company) throw new Error("Not found");

    // Verified company identity (KYB). The verification table is NOT publicly
    // readable — register/VAT numbers are sensitive identifiers. We read it
    // server-side and publish only the badge facts: verified yes/no, the
    // verified domain and the country.
    let verified = false;
    let verification: {
      country: string | null;
      domain: string | null;
    } | null = null;
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: ver } = await (supabaseAdmin as any)
        .from("company_verifications")
        .select("country, domain, status")
        .eq("company_id", company.id)
        .maybeSingle();
      if (ver) {
        verified = ver.status === "verified";
        verification = {
          country: ver.country ?? null,
          domain: ver.domain ?? null,
        };
      }
    } catch {
      /* ignore */
    }

    // Behavioural reputation — published only once there's enough history.
    let reputation: {
      responseRate: number | null;
      medianResponseHours: number | null;
      completionRate: number | null;
    } | null = null;
    try {
      const { computeReputation } = await import("@/lib/reputation.server");
      const rep = await computeReputation(company.id);
      if (rep.established) {
        reputation = {
          responseRate: rep.responseRate,
          medianResponseHours: rep.medianResponseHours,
          completionRate: rep.completionRate,
        };
      }
    } catch {
      /* best-effort */
    }

    return { company: { ...company, verified, verification, reputation } };
  });

// Public, unauthenticated lead capture from a twin's marketplace page.
// Written with the owner's user_id so the lead shows up in their dashboard.
export const submitVisitorLead = createServerFn({ method: "POST" })
  .inputValidator((d) =>
    z
      .object({
        companyId: z.string().uuid(),
        conversationId: z.string().uuid().optional(),
        name: z.string().trim().min(1).max(120),
        email: z.string().trim().email().max(200),
        message: z.string().trim().max(1000).optional(),
        phone: z.string().trim().max(60).optional(),
        company: z.string().trim().max(200).optional(),
        address: z.string().trim().max(400).optional(),
        city: z.string().trim().max(120).optional(),
        postalCode: z.string().trim().max(40).optional(),
        country: z.string().trim().max(120).optional(),
        // Honeypot: real users never fill this hidden field.
        website: z.string().max(0).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    await assertPublicRateLimit("marketplace:lead", 5);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: company } = await supabaseAdmin
      .from("companies")
      .select("id, user_id, is_public, name")
      .eq("id", data.companyId)
      .maybeSingle();
    if (!company || !company.is_public) throw new Error("Twin not found");

    // Basic abuse guard: cap unauthenticated leads per company per hour.
    const hourAgo = new Date(Date.now() - 3600_000).toISOString();
    const { count } = await supabaseAdmin
      .from("leads")
      .select("id", { count: "exact", head: true })
      .eq("company_id", company.id)
      .gte("created_at", hourAgo);
    if ((count ?? 0) >= 50) throw new Error("Too many requests, please try again later");

    const { data: dupe } = await supabaseAdmin
      .from("leads")
      .select("id")
      .eq("company_id", company.id)
      .eq("email", data.email)
      .limit(1)
      .maybeSingle();
    if (!dupe) {
      const { error } = await supabaseAdmin.from("leads").insert({
        company_id: company.id,
        user_id: company.user_id,
        name: data.name,
        email: data.email,
        question: data.message ?? null,
        intent_score: 90,
        lead_score: 90,
        status: "hot",
      });
      if (error) throw error;
      const { notifyNewLead } = await import("@/lib/notify.server");
      await notifyNewLead({
        ownerUserId: company.user_id,
        companyName: company.name,
        leadName: data.name,
        leadEmail: data.email,
        question: data.message ?? null,
        status: "hot",
      });
      const { dispatchLeadWebhook } = await import("@/lib/webhooks.server");
      await dispatchLeadWebhook(company.id, {
        name: data.name,
        email: data.email,
        question: data.message ?? null,
        intent_score: 90,
        lead_score: 90,
        status: "hot",
        source: "contact_form",
      });
    }

    if (data.conversationId) {
      await supabaseAdmin
        .from("conversations")
        .update({ visitor_name: data.name, visitor_email: data.email })
        .eq("id", data.conversationId)
        .eq("company_id", company.id)
        .eq("source", "visitor");
    }

    // If the visitor filled in full contact details (address, phone, company),
    // persist them to the editable CRM record so they show up in the customer
    // file. Basic name+email leads are left as derived contacts.
    const hasDetails =
      data.phone || data.company || data.address || data.city || data.postalCode || data.country;
    if (hasDetails) {
      try {
        await (supabaseAdmin as any).from("crm_contacts").upsert(
          {
            company_id: company.id,
            email: data.email.trim().toLowerCase(),
            name: data.name,
            phone: data.phone ?? null,
            company_name: data.company ?? null,
            address: data.address ?? null,
            city: data.city ?? null,
            postal_code: data.postalCode ?? null,
            country: data.country ?? null,
            archived: false,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "company_id,email" },
        );
      } catch {
        /* crm_contacts may not exist yet — the lead is already captured */
      }
    }

    return { ok: true };
  });
